import React from "react";
import StatWrapper from "../shared/StatWrapper";
import Heading from "hardcover-ui/components/Heading";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "hardcover-ui/components/Tooltip";
import Badge from "hardcover-ui/components/Badge";
import { useChartTheme } from "../contexts/ChartThemeContext";
import { usePage } from "@inertiajs/react";
import { UserStatsPageProps } from "pages/clientverse/users/stats/index.props";

interface BooksByPageCountStatProps {
  size?: "sm" | "md" | "lg" | "xl";
}

const BooksByPageCountStat: React.FC<BooksByPageCountStatProps> = ({
  size,
}) => {
  const { user, isOwner, stats } = usePage<UserStatsPageProps>().props;
  const { pageCountBreakdown: data } = stats;
  const { theme } = useChartTheme();

  if (!data.pages || data.pages.length === 0) {
    return (
      <StatWrapper title="Books By Page Count" size={size}>
        <Heading size="sm" className="mb-2">
          No page count data available
        </Heading>
      </StatWrapper>
    );
  }

  const maxCount = Math.max(...data.pages.map((item) => item.value || 0));
  const top = [...data.pages].sort((a, b) => b.value - a.value)[0];

  const getBarColor = () => {
    return theme === "colorful"
      ? "var(--chart-colorful-2)"
      : "var(--chart-mono-2)";
  };

  return (
    <StatWrapper title="Books By Page Count" size={size}>
      <div className="flex flex-col h-full w-full ">
        <Heading size="sm" className="mb-2">
          Most of {isOwner ? "your" : `${user.name}'s`} books are between{" "}
          {top?.label || "unknown"} pages
        </Heading>
        <Badge variant="secondary" size="sm" className="mb-4 w-fit">
          Average page count:{" "}
          {data.averagePages ? Math.round(data.averagePages) : "N/A"}
        </Badge>
        <div className="flex-grow flex flex-col">
          <div className="h-64 flex items-end justify-between">
            {data.pages.map((item) => (
              <Tooltip key={item.label}>
                <TooltipTrigger asChild>
                  <div className="flex flex-col items-center justify-end h-full">
                    <div className="text-xs text-secondary mb-1">
                      {item.value || 0}
                    </div>
                    <div
                      className="w-4 md:w-5 lg:w-8 xl:w-12  hover:opacity-80 transition-opacity rounded-t-md"
                      style={{
                        height: `${((item.value || 0) / maxCount) * 100}%`,
                        backgroundColor: getBarColor(),
                      }}
                    ></div>
                    <div className="text-tertiary text-xs mt-1 cursor-help">
                      {item.label}
                    </div>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{item.value || 0} books</p>
                  <p>
                    {data.totalBooks
                      ? (((item.value || 0) / data.totalBooks) * 100).toFixed(1)
                      : "0.0"}
                    % of your books
                  </p>
                  <p>Page range: {item.label}</p>
                </TooltipContent>
              </Tooltip>
            ))}
          </div>
        </div>
      </div>
    </StatWrapper>
  );
};

export default BooksByPageCountStat;
